import type { SeverityLevel } from '@sentry/react'
import { HTTPError } from 'ky'

// Body sent by the zod middleware when the API rejects the request shape (see @core/middleware/zod.middleware).
const INVALID_PAYLOAD_BODY = 'Invalid Payload'

/**
 * Rejection of a `toastPromise` whose failure has already been shown to the user.
 * The rejection is kept as control flow (react-hook-form `isSubmitSuccessful`, code after `await`),
 * but Sentry drops it in `beforeSend`: reporting is decided once, up front, by `displayedErrorReportLevel`.
 */
export class ToastedError extends Error {
  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause), { cause })
    this.name = 'ToastedError'
  }
}

/** First `HTTPError` in the `cause` chain: mutation hooks wrap the ky error (`new Error('Failed to …', { cause })`). */
function findHttpError(error: unknown): HTTPError | undefined {
  let current = error

  while (current instanceof Error) {
    if (current instanceof HTTPError) return current

    current = current.cause
  }

  return undefined
}

async function isInvalidPayload(response: Response) {
  // A hook that already read the body to build its business error leaves it unreadable: that body was a business message.
  if (response.bodyUsed) return false

  // Read from a clone: the toast `error` callbacks read `error.response.text()` themselves.
  return (await response.clone().text()) === INVALID_PAYLOAD_BODY
}

/** Sentry level at which an error already displayed to the user must still be reported, `undefined` to skip it. */
export async function displayedErrorReportLevel(error: unknown): Promise<SeverityLevel | undefined> {
  const httpError = findHttpError(error)

  // No HTTP failure behind it: a parsing error, a TypeError… a real bug.
  if (!httpError) return 'error'

  const { status } = httpError.response

  // 5xx are already captured by httpClientIntegration.
  if (status >= 500) return undefined

  if (status === 401 || status === 403) return 'warning'

  if (status === 400 && (await isInvalidPayload(httpError.response))) return 'error'

  // Other 4xx are business errors (conflict, unassigned operator…) the user has already been told about.
  return undefined
}
